/**
 * AssetImage — Tiptap custom node pentru imagini E2E.
 *
 * Persistat în HTML ca:
 *   <img data-asset-id="UUID" alt="filename" data-width="420" data-align="left">
 *
 * NodeView React cu:
 *   - fetch asset criptat → decrypt client → blob URL cache global
 *   - 5 moduri layout: full (default), center, left-wrap, right-wrap, inline
 *   - resize prin drag pe colțul dreapta-jos (vizibil în modul edit la selecție)
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

import { assetsApi } from '@/api/assets';
import { decryptWithWrappedKey } from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import { registerSessionCleanup } from '@/stores/sessionCleanup';

export type ImageAlign = 'center' | 'left' | 'right';

const VALID_ALIGN: ImageAlign[] = ['center', 'left', 'right'];
const MIN_WIDTH = 80;
const MAX_WIDTH = 1600;

function parseAlign(value: string | null): ImageAlign {
  if (value && (VALID_ALIGN as string[]).includes(value)) {
    return value as ImageAlign;
  }
  // Compat: valoarea veche 'full' e interpretată ca center (block 100%).
  return 'center';
}

function parseWidth(value: string | null): number | null {
  if (!value) return null;
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < MIN_WIDTH || n > MAX_WIDTH) return null;
  return n;
}

export const AssetImageNode = Node.create({
  name: 'assetImage',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      assetId: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-asset-id'),
      },
      alt: {
        default: '',
        parseHTML: (el) => el.getAttribute('alt'),
      },
      width: {
        default: null,
        parseHTML: (el) => parseWidth(el.getAttribute('data-width')),
      },
      align: {
        default: 'center' as ImageAlign,
        parseHTML: (el) => parseAlign(el.getAttribute('data-align')),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-asset-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs: Record<string, string> = {
      'data-asset-id': HTMLAttributes.assetId,
      alt: HTMLAttributes.alt ?? '',
    };
    if (HTMLAttributes.width) attrs['data-width'] = String(HTMLAttributes.width);
    if (HTMLAttributes.align && HTMLAttributes.align !== 'center') {
      attrs['data-align'] = HTMLAttributes.align;
    }
    return ['img', mergeAttributes(attrs)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AssetImageView);
  },
});

// Cache global blob URL-uri per asset ID. Conține imagini decriptate, deci e
// golit (și URL-urile revocate) la încheierea sesiunii.
const blobCache = new Map<string, string>();

registerSessionCleanup(() => {
  for (const url of blobCache.values()) URL.revokeObjectURL(url);
  blobCache.clear();
});

function AssetImageView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const { t } = useTranslation();
  const assetId = node.attrs.assetId as string;
  const alt = (node.attrs.alt as string) || t('notes_blocks.asset_image.alt_fallback');
  const width = node.attrs.width as number | null;
  const align = (node.attrs.align as ImageAlign) || 'center';
  const keyPair = useAuthStore((s) => s.keyPair);

  const [url, setUrl] = useState<string | null>(blobCache.get(assetId) ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!assetId || !keyPair || loadedRef.current) return;
    if (blobCache.has(assetId)) {
      setUrl(blobCache.get(assetId)!);
      return;
    }
    loadedRef.current = true;
    (async () => {
      try {
        const detail = await assetsApi.get(assetId);
        const plain = await decryptWithWrappedKey(detail, keyPair);
        const blob = new Blob([new Uint8Array(plain)], { type: detail.mime_type });
        const objectUrl = URL.createObjectURL(blob);
        blobCache.set(assetId, objectUrl);
        setUrl(objectUrl);
      } catch (e) {
        setErr(e instanceof Error ? e.message : t('notes_blocks.asset_image.error'));
      }
    })();
  }, [assetId, keyPair, t]);

  const isEditable = editor.isEditable;
  const showHandles = isEditable && selected && !!url;
  const effectiveWidth = dragging && dragWidth != null ? dragWidth : width;

  // corner: 'br' | 'bl' | 'tr' | 'tl'. `signX`/`signY` indică direcția în
  // care drag-ul „expand" imaginea (pozitiv = mai mare).
  const startResize = (
    ev: React.PointerEvent<HTMLDivElement>,
    corner: 'br' | 'bl' | 'tr' | 'tl',
  ) => {
    if (!imgRef.current) return;
    ev.preventDefault();
    ev.stopPropagation();
    const signX = corner === 'br' || corner === 'tr' ? 1 : -1;
    const signY = corner === 'br' || corner === 'bl' ? 1 : -1;
    const startX = ev.clientX;
    const startY = ev.clientY;
    const naturalW = imgRef.current.naturalWidth || imgRef.current.clientWidth;
    const naturalH = imgRef.current.naturalHeight || imgRef.current.clientHeight;
    const aspectRatio = naturalH > 0 ? naturalW / naturalH : 1;
    const startW = width ?? imgRef.current.clientWidth ?? naturalW;
    setDragging(true);
    setDragWidth(startW);
    (ev.target as HTMLElement).setPointerCapture(ev.pointerId);

    const onMove = (e: PointerEvent) => {
      const dxEff = (e.clientX - startX) * signX;
      const dyEff = (e.clientY - startY) * signY * aspectRatio;
      const delta = Math.abs(dyEff) > Math.abs(dxEff) ? dyEff : dxEff;
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startW + delta));
      setDragWidth(Math.round(next));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDragging((wasDragging) => {
        if (wasDragging) {
          setDragWidth((finalW) => {
            if (finalW != null) updateAttributes({ width: finalW });
            return null;
          });
        }
        return false;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <NodeViewWrapper
      as="div"
      className={wrapperClassForAlign(align)}
      data-selected={selected ? 'true' : 'false'}
      contentEditable={false}
    >
      <div
        className={`relative inline-block align-top ${
          selected && isEditable
            ? 'ring-2 ring-[var(--accent)] ring-offset-2 ring-offset-[var(--bg)]'
            : ''
        }`}
        style={
          effectiveWidth
            ? { width: `${effectiveWidth}px`, maxWidth: '100%' }
            : undefined
        }
      >
        {err ? (
          <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1">
            {t('notes_blocks.asset_image.unavailable', { error: err })}
          </div>
        ) : url ? (
          <img
            ref={imgRef}
            src={url}
            alt={alt}
            className="block w-full h-auto rounded-md border border-[var(--border-strong)] select-none"
            draggable={false}
          />
        ) : (
          <div className="text-xs text-[var(--text-muted)] italic py-4 px-6 border border-dashed border-[var(--border)] rounded">
            {t('notes_blocks.asset_image.loading')}
          </div>
        )}
        {showHandles && (
          <>
            <ResizeHandle corner="tl" onPointerDown={(e) => startResize(e, 'tl')} />
            <ResizeHandle corner="tr" onPointerDown={(e) => startResize(e, 'tr')} />
            <ResizeHandle corner="bl" onPointerDown={(e) => startResize(e, 'bl')} />
            <ResizeHandle corner="br" onPointerDown={(e) => startResize(e, 'br')} />
          </>
        )}
        {dragging && effectiveWidth != null && (
          <div
            className="absolute -top-6 right-0 px-2 py-0.5 rounded font-mono text-[10px] pointer-events-none select-none"
            style={{
              background: 'var(--accent)',
              color: 'var(--bg)',
              whiteSpace: 'nowrap',
            }}
          >
            {effectiveWidth}px
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}

/**
 * Clasele aplicate pe wrapper-ul NodeViewWrapper (block-level în document).
 *
 * Notă: float (`left`/`right`) face ca textul din paragrafele următoare să
 * curgă în jurul imaginii. `inline` o pune lângă textul curent. `center` +
 * `full` sunt block centered / 100%.
 */
function wrapperClassForAlign(align: ImageAlign): string {
  switch (align) {
    case 'left':
      return 'my-2 float-left mr-4 mb-2 clear-left max-w-[60%]';
    case 'right':
      return 'my-2 float-right ml-4 mb-2 clear-right max-w-[60%]';
    case 'center':
    default:
      return 'my-3 mx-auto text-center clear-both';
  }
}

/**
 * Handle pătrat pe unul din cele 4 colțuri. Poziționat ușor „peste" marginea
 * imaginii (offset negativ) ca să fie vizibil și ușor de prins cu mouse.
 */
function ResizeHandle({
  corner,
  onPointerDown,
}: {
  corner: 'br' | 'bl' | 'tr' | 'tl';
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
}) {
  const posClass = {
    tl: '-left-1.5 -top-1.5',
    tr: '-right-1.5 -top-1.5',
    bl: '-left-1.5 -bottom-1.5',
    br: '-right-1.5 -bottom-1.5',
  }[corner];
  const { t } = useTranslation();
  const cursor =
    corner === 'tl' || corner === 'br' ? 'cursor-nwse-resize' : 'cursor-nesw-resize';

  return (
    <div
      onPointerDown={onPointerDown}
      className={`absolute ${posClass} w-3 h-3 rounded-sm ${cursor} z-10`}
      style={{
        background: 'var(--bg)',
        border: '2px solid var(--accent)',
        touchAction: 'none',
      }}
      title={t('notes_blocks.asset_image.resize_title')}
    />
  );
}
